import React, { useRef } from 'react';
import { Square, Circle } from 'lucide-react';

/**
 * Images & Posts canvas — a fixed-aspect-ratio surface that renders the
 * active page's elements as absolutely-positioned boxes (percentages of
 * the canvas, so they stay correct at any on-screen size). This is a
 * Level 1/2 layout tool, not a rendering engine: nothing here produces a
 * server-side flattened image, it only records where each element sits.
 */
export function DesignCanvas({ page, aspectRatio, selectedElementId, onSelectElement, onUpdateElement, onDeselect }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  const startDrag = (e, el, mode) => {
    e.stopPropagation();
    onSelectElement(el.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { mode, elId: el.id, startX: e.clientX, startY: e.clientY, orig: { x: el.x, y: el.y, w: el.w, h: el.h }, rectW: rect.width, rectH: rect.height };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dxPct = ((ev.clientX - d.startX) / d.rectW) * 100;
      const dyPct = ((ev.clientY - d.startY) / d.rectH) * 100;
      if (d.mode === 'move') {
        onUpdateElement(d.elId, {
          x: Math.max(0, Math.min(100 - d.orig.w, d.orig.x + dxPct)),
          y: Math.max(0, Math.min(100 - d.orig.h, d.orig.y + dyPct)),
        });
      } else {
        onUpdateElement(d.elId, {
          w: Math.max(4, Math.min(100 - d.orig.x, d.orig.w + dxPct)),
          h: Math.max(4, Math.min(100 - d.orig.y, d.orig.h + dyPct)),
        });
      }
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const [arW, arH] = (aspectRatio || '1:1').split(':').map(Number);

  return (
    <div className="w-full h-full flex items-center justify-center p-4" data-testid="design-canvas-wrapper">
      <div
        ref={canvasRef}
        onMouseDown={onDeselect}
        className="relative bg-white shadow-2xl overflow-hidden"
        style={{ aspectRatio: `${arW} / ${arH}`, height: '100%', width: 'auto', maxWidth: '100%', maxHeight: '100%', background: page?.background || '#ffffff' }}
        data-testid="design-canvas"
      >
        {(page?.elements || []).map((el) => (
          <div
            key={el.id}
            onMouseDown={(e) => startDrag(e, el, 'move')}
            className={`absolute cursor-move select-none ${el.id === selectedElementId ? 'outline outline-2 outline-[#3A7BFF]' : 'outline outline-1 outline-transparent hover:outline-white/40'}`}
            style={{ left: `${el.x}%`, top: `${el.y}%`, width: `${el.w}%`, height: `${el.h}%`, display: el.visible === false ? 'none' : undefined }}
            data-testid={`design-element-${el.id}`}
          >
            {el.type === 'text' && (
              <div
                className="w-full h-full flex items-center overflow-hidden break-words"
                style={{ fontSize: `${el.fontSize || 24}px`, color: el.color || '#111111', fontWeight: el.bold ? 700 : 400, justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start', textAlign: el.align || 'left' }}
              >
                {el.text || 'Text'}
              </div>
            )}
            {el.type === 'image' && (
              el.src
                ? <img src={el.src} alt="" className="w-full h-full pointer-events-none" style={{ objectFit: el.objectFit || 'cover' }} />
                : <div className="w-full h-full bg-[#D9DEE6] flex items-center justify-center text-[10px] text-[#8A93A6]">Image</div>
            )}
            {el.type === 'shape' && (
              <div className="w-full h-full flex items-center justify-center" style={{ background: el.shapeType === 'line' ? 'transparent' : (el.fill || '#3A7BFF'), borderRadius: el.shapeType === 'circle' ? '50%' : 0 }}>
                {el.shapeType === 'line' && <div className="w-full" style={{ height: 4, background: el.fill || '#3A7BFF' }} />}
              </div>
            )}
            {el.id === selectedElementId && (
              <div
                onMouseDown={(e) => startDrag(e, el, 'resize')}
                className="absolute -right-1.5 -bottom-1.5 h-3 w-3 rounded-full bg-[#3A7BFF] border border-white cursor-se-resize"
                data-testid={`design-element-resize-${el.id}`}
              />
            )}
          </div>
        ))}
        {(!page?.elements || page.elements.length === 0) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#8A93A6]" data-testid="design-canvas-empty">
            <Square className="h-5 w-5 opacity-40" />
            <Circle className="h-3 w-3 opacity-20 -mt-4 ml-6" />
            <p className="text-xs">Add text, an image or a shape to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
